import * as React from "react";
import { useNavigate as useRouter, useSearch as useSearchParams } from "../../router";
import { DataTable } from "../data-table";
import { StatStrip, type StatStripItem } from "../dashboard/stat-strip";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { ScopedSearchButton } from "../scoped-search-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import { Table2 } from "../../icons";
import { cn } from "../../utils";
import type {
  DbObjectDto,
  DbConnectionInfoDto,
} from "../../api/contracts/database/schema";
import { formatBytes, formatCount, GROUP_LABELS, GROUP_ORDER } from "./format";

/**
 * The database object inventory — ONE integrated `DataTable` Card (the CRM
 * portfolio-view pattern), not a stack of floating surfaces:
 *  - the corpus metrics are the table's caption band (`StatStrip variant="table"`
 *    in `summary`),
 *  - the domain facet is a `<Select>` filter band stacked under it,
 *  - search rides the integrated header (`headerAction`), with `title`/`count`,
 *  - the rows sort + paginate inside the same Card, with a row-count bar.
 *
 * Every object — tables, views, virtual tables — is listed; system objects
 * (sqlite_*, _migrations, FTS shadows) carry a "system" badge, never hidden
 * (transparency is the point). Clicking a table opens the Table Editor; a
 * view/system object opens the Schema detail.
 */
export function TableInventory({
  objects,
  info,
  linkMode = "editor",
}: {
  objects: DbObjectDto[];
  /** Connection facts, for the "On disk" metric. Optional. */
  info?: DbConnectionInfoDto;
  /** "editor": tables → Table Editor; others → Schema. "schema": all → Schema. */
  linkMode?: "editor" | "schema";
}) {
  const router = useRouter();
  const [domain, setDomain] = React.useState("all");
  // Scoped-search model (Issue 19): the dock owns the query; this inventory
  // mirrors it from the URL `?q=` and filters its own rows. The header's
  // magnifying-glass button focuses the dock (pre-scoped to Tables).
  const searchParams = useSearchParams();
  const q = searchParams.get("q") ?? "";

  // Domains present in the data, in the canonical order (no empty options).
  const presentGroups = React.useMemo(() => {
    const seen = new Set(objects.map((o) => o.group));
    const ordered = GROUP_ORDER.filter((g) => seen.has(g));
    const extras = [...seen].filter((g) => !GROUP_ORDER.includes(g)).sort();
    return [...ordered, ...extras];
  }, [objects]);

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return objects.filter((o) => {
      if (domain !== "all" && o.group !== domain) return false;
      if (
        needle &&
        !o.name.toLowerCase().includes(needle) &&
        !o.group.toLowerCase().includes(needle) &&
        !o.type.toLowerCase().includes(needle)
      ) {
        return false;
      }
      return true;
    });
  }, [objects, domain, q]);

  // KPI cells computed over the FULL corpus (not the filtered subset).
  const kpis: StatStripItem[] = [
    {
      title: "Objects",
      value: formatCount(info?.objectCount ?? objects.length),
      description: "tables · views · indexes",
    },
    {
      title: "Tables",
      value: formatCount(objects.filter((o) => o.type === "table").length),
      description: "editable",
    },
    {
      title: "Total rows",
      value: formatCount(
        objects.reduce((s, o) => s + (o.rowCount > 0 ? o.rowCount : 0), 0),
      ),
      description: "across all tables",
    },
    info
      ? {
          title: "On disk",
          value: formatBytes(info.sizeBytes),
          description: `${formatCount(info.pageCount)} pages`,
        }
      : {
          title: "System",
          value: formatCount(objects.filter((o) => o.system).length),
          description: "internal objects",
        },
  ];

  function open(o: DbObjectDto) {
    if (linkMode === "editor" && o.type === "table" && !o.system) {
      router.push(`/database/editor/${encodeURIComponent(o.name)}`);
    } else {
      router.push(`/database/schema/${encodeURIComponent(o.name)}`);
    }
  }

  const filterBand = (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-2.5">
      <Select value={domain} onValueChange={setDomain}>
        <SelectTrigger className="h-7 w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All domains</SelectItem>
          {presentGroups.map((g) => (
            <SelectItem key={g} value={g}>
              {GROUP_LABELS[g] ?? g}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {domain !== "all" ? (
        <Button variant="outline" size="sm" onClick={() => setDomain("all")}>
          Clear
        </Button>
      ) : null}
    </div>
  );

  return (
    <DataTable
      title="Objects"
      count={filtered.length}
      headerAction={<ScopedSearchButton label="Search objects" />}
      summary={
        <>
          <StatStrip variant="table" items={kpis} />
          {filterBand}
        </>
      }
      rows={filtered}
      rowKey={(o: DbObjectDto) => o.name}
      onRowClick={open}
      strongHeader
      pageSize={25}
      initialSort={{ key: "rows", dir: "desc" }}
      empty={q ? `No objects match “${q}”.` : "No objects."}
      columns={[
        {
          key: "name",
          header: "Name",
          sort: (a: DbObjectDto, b: DbObjectDto) => a.name.localeCompare(b.name),
          render: (o: DbObjectDto) => (
            <span className="flex items-center gap-2">
              <Table2 className="size-3.5 shrink-0 text-muted-foreground/60" />
              <span className="font-mono text-foreground">{o.name}</span>
              {o.system ? (
                <Badge variant="outline" className="text-muted-foreground">
                  system
                </Badge>
              ) : null}
            </span>
          ),
        },
        {
          key: "group",
          header: "Domain",
          width: "w-40",
          sort: (a: DbObjectDto, b: DbObjectDto) => a.group.localeCompare(b.group),
          render: (o: DbObjectDto) => (
            <span className="text-muted-foreground">
              {GROUP_LABELS[o.group] ?? o.group}
            </span>
          ),
        },
        {
          key: "type",
          header: "Type",
          width: "w-24",
          sort: (a: DbObjectDto, b: DbObjectDto) => a.type.localeCompare(b.type),
          render: (o: DbObjectDto) => (
            <span className="text-muted-foreground">{o.type}</span>
          ),
        },
        {
          key: "rows",
          header: "Rows",
          width: "w-28",
          defaultDesc: true,
          sort: (a: DbObjectDto, b: DbObjectDto) => a.rowCount - b.rowCount,
          render: (o: DbObjectDto) => <RowsCell rows={o.rowCount} />,
        },
      ]}
    />
  );
}

/** Row-count cell — a plain right-aligned number (a view/system object reads "—"). */
function RowsCell({ rows }: { rows: number }) {
  if (rows < 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className={cn(
        "tabular-nums",
        rows === 0 ? "text-muted-foreground/50" : "text-foreground",
      )}
    >
      {formatCount(rows)}
    </span>
  );
}
